/**
 * /clientes/nuevo  → modo create.
 * /clientes/:id    → modo edit (pre-fill via GET).
 *
 * Mismo formulario para crear y editar. PII (email/phone) se cifra
 * at-rest backend-side; este form los manda en claro vía HTTPS.
 */

import { useEffect, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { useMutation, useQuery } from '@tanstack/react-query'
import { ArrowLeft, Loader2, Save, Trash2 } from 'lucide-react'
import { toast } from 'sonner'

import { AppShell } from '@/components/layout/app-shell'
import { useAuthContext } from '@/hooks/use-auth-context'
import { customerApi, lookupApi, type CustomerInput } from '@/lib/api'
import { DEPARTMENTS, ID_TYPES, FISCAL_RESPONSIBILITIES, ROUTES, TAX_REGIMES } from '@/lib/constants'

const EMPTY: CustomerInput = {
  identification_type: '13',
  identification_number: '',
  verification_digit: '',
  legal_name: '',
  trade_name: '',
  address: '',
  city: '',
  department: '',
  country: 'CO',
  email: '',
  phone: '',
  fiscal_responsibilities: ['R-99-PN'],
  tax_regime: '49',
}

export function CustomerEditPage() {
  const { id } = useParams<{ id?: string }>()
  const isEdit = !!id && id !== 'nuevo'
  const { activeOrgId, isLoading: authLoading } = useAuthContext()
  const nav = useNavigate()
  const [s, setS] = useState<CustomerInput>(EMPTY)

  // Pre-fill si estamos editando.
  const get = useQuery({
    queryKey: ['customer', activeOrgId, id],
    queryFn: () => customerApi.get(activeOrgId!, id!),
    enabled: !!activeOrgId && isEdit,
  })

  useEffect(() => {
    if (get.data) {
      setS({
        identification_type: get.data.identification_type,
        identification_number: get.data.identification_number,
        verification_digit: get.data.verification_digit ?? '',
        legal_name: get.data.legal_name,
        trade_name: get.data.trade_name ?? '',
        address: get.data.address,
        city: get.data.city,
        department: get.data.department,
        country: get.data.country,
        email: get.data.email ?? '',
        phone: get.data.phone ?? '',
        fiscal_responsibilities: get.data.fiscal_responsibilities,
        tax_regime: get.data.tax_regime ?? '49',
      })
    }
  }, [get.data])

  const save = useMutation({
    mutationFn: () =>
      isEdit ? customerApi.update(activeOrgId!, id!, s) : customerApi.create(activeOrgId!, s),
    onSuccess: () => {
      toast.success(isEdit ? 'Cliente actualizado' : 'Cliente creado')
      nav(ROUTES.CUSTOMERS)
    },
    onError: (err: unknown) => {
      const e = err as { response?: { data?: { message?: string } }; message?: string }
      toast.error(`Error: ${e?.response?.data?.message ?? e?.message ?? 'unknown'}`)
    },
  })

  const archive = useMutation({
    mutationFn: () => customerApi.archive(activeOrgId!, id!),
    onSuccess: () => {
      toast.success('Cliente archivado')
      nav(ROUTES.CUSTOMERS)
    },
    onError: (err: unknown) => {
      const e = err as { response?: { data?: { message?: string } } }
      toast.error(`No se pudo archivar: ${e?.response?.data?.message ?? 'error'}`)
    },
  })

  if (authLoading) {
    return (
      <AppShell>
        <div className="flex items-center gap-2 p-6 text-sm text-fg-muted">
          <Loader2 className="h-4 w-4 animate-spin" /> Cargando contexto...
        </div>
      </AppShell>
    )
  }

  if (!activeOrgId) {
    return (
      <AppShell>
        <div className="rounded-xl border border-error/30 bg-error-tint p-6 text-sm text-error-fg">
          Sin organización activa. <a href="/login" className="underline">Iniciar sesión</a>
        </div>
      </AppShell>
    )
  }

  const isNIT = s.identification_type === '31'
  // AdditionalAccountID (DIAN tabla 13.2.7): "1" = persona jurídica
  // (NIT), "2" = persona natural (CC/CE/TI/Pasaporte). Solo visual
  // en este form — el backend lo deriva del identification_type al
  // construir el UBL.
  const additionalAccountID = isNIT ? '1' : '2'

  // RUES NIT lookup debounced. Solo cuando el ID es NIT y mode=create
  // (en edit el user ya conoce los datos).
  const isCreate = !id
  // eslint-disable-next-line react-hooks/rules-of-hooks
  useEffect(() => {
    if (!isNIT || !isCreate) return
    const nit = (s.identification_number ?? '').trim()
    if (nit.length < 9) return
    const handle = setTimeout(async () => {
      try {
        const r = await lookupApi.nit(nit)
        if (r.found) {
          setS((prev) => ({
            ...prev,
            // Solo autocompleta campos vacíos para no sobrescribir.
            legal_name: prev.legal_name || r.razon_social || '',
            email: prev.email || r.email_corporativo || '',
            address: prev.address || r.direccion || '',
            department: prev.department || r.departamento || '',
            city: prev.city || r.municipio || '',
          }))
        }
      } catch {
        // RUES failure silencioso — el user llena a mano.
      }
    }, 600)
    return () => clearTimeout(handle)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [s.identification_number, isNIT, isCreate])
  // Email regex pragmática (no full RFC 5322 — DIAN no rechaza por
  // tilde/símbolos raros sino por estructura básica).
  const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  // AT FAK55 (R cuando adquiriente es facturador): ElectronicMail
  // obligatorio en B2B. Para consumidor final puro no facturador
  // técnicamente es opcional, pero el 95% de los casos colombianos
  // son B2B; se hace obligatorio en el form. Si emerge un caso B2C
  // puro lo flexibilizamos.
  const valid =
    s.identification_number &&
    s.legal_name &&
    s.address &&
    s.city &&
    s.department &&
    (s.email ?? '').length > 0 &&
    emailRe.test(s.email ?? '') &&
    (!isNIT || /^\d$/.test(s.verification_digit ?? ''))

  return (
    <AppShell>
      <div className="mx-auto max-w-2xl">
        <Link
          to={ROUTES.CUSTOMERS}
          className="inline-flex items-center gap-1.5 text-sm text-fg-muted hover:text-fg-primary"
        >
          <ArrowLeft className="h-4 w-4" /> Volver
        </Link>
        <h1 className="mt-2 text-3xl font-bold">
          {isEdit ? 'Editar cliente' : 'Nuevo cliente'}
        </h1>

        {get.isLoading && (
          <div className="mt-6 flex items-center gap-2 text-sm text-fg-muted">
            <Loader2 className="h-4 w-4 animate-spin" /> Cargando...
          </div>
        )}

        <form
          onSubmit={(e) => {
            e.preventDefault()
            if (valid) save.mutate()
          }}
          className="mt-6 space-y-4 rounded-xl border border-border bg-bg-surface p-6"
        >
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Tipo de identificación *" htmlFor="id_type">
              <select
                id="id_type"
                value={s.identification_type}
                onChange={(e) => setS({ ...s, identification_type: e.target.value })}
                className={inputCls}
              >
                {ID_TYPES.map((t) => (
                  <option key={t.code} value={t.code}>
                    {t.label}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Número *" htmlFor="id_number">
              <input
                id="id_number"
                type="text"
                value={s.identification_number}
                onChange={(e) =>
                  setS({ ...s, identification_number: e.target.value.replace(/\s/g, '') })
                }
                className={inputCls}
              />
            </Field>
          </div>

          {isNIT && (
            <Field label="DV (NIT) *" htmlFor="dv">
              <input
                id="dv"
                type="text"
                maxLength={1}
                value={s.verification_digit ?? ''}
                onChange={(e) =>
                  setS({ ...s, verification_digit: e.target.value.replace(/\D/g, '') })
                }
                className={`${inputCls} w-20`}
              />
            </Field>
          )}

          <Field label="Razón social *" htmlFor="legal_name">
            <input
              id="legal_name"
              type="text"
              value={s.legal_name}
              onChange={(e) => setS({ ...s, legal_name: e.target.value })}
              className={inputCls}
            />
          </Field>

          <Field label="Nombre comercial" htmlFor="trade_name">
            <input
              id="trade_name"
              type="text"
              value={s.trade_name ?? ''}
              onChange={(e) => setS({ ...s, trade_name: e.target.value })}
              className={inputCls}
            />
          </Field>

          <Field label="Dirección *" htmlFor="address">
            <input
              id="address"
              type="text"
              value={s.address}
              onChange={(e) => setS({ ...s, address: e.target.value })}
              className={inputCls}
            />
          </Field>

          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Ciudad *" htmlFor="city">
              <input
                id="city"
                type="text"
                value={s.city}
                onChange={(e) => setS({ ...s, city: e.target.value })}
                className={inputCls}
              />
            </Field>
            <Field label="Departamento *" htmlFor="department">
              <select
                id="department"
                value={s.department}
                onChange={(e) => setS({ ...s, department: e.target.value })}
                className={inputCls}
              >
                <option value="">Selecciona...</option>
                {DEPARTMENTS.map((d) => (
                  <option key={d} value={d}>
                    {d}
                  </option>
                ))}
              </select>
            </Field>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Email *" htmlFor="email">
              <input
                id="email"
                type="email"
                required
                aria-required="true"
                value={s.email ?? ''}
                onChange={(e) => setS({ ...s, email: e.target.value })}
                className={inputCls}
              />
            </Field>
            <Field label="Teléfono" htmlFor="phone">
              <input
                id="phone"
                type="tel"
                value={s.phone ?? ''}
                onChange={(e) => setS({ ...s, phone: e.target.value })}
                className={inputCls}
              />
            </Field>
          </div>
          <p className="text-xs text-fg-muted">
            <strong>Email obligatorio</strong> — DIAN exige{' '}
            <code>cac:Contact/cbc:ElectronicMail</code> (Anexo Técnico v1.9
            FAK55) en toda factura B2B con adquirente facturador electrónico.
            Para consumidor final puro (B2C) puede usarse{' '}
            <code>noreply@taxor.co</code> de momento.
          </p>
          <p className="text-xs text-fg-muted">
            Email y teléfono se cifran at-rest con HKDF per-tenant. Solo accesibles
            descifrados al servirlos a tu propia UI.
          </p>
          <p className="text-xs text-fg-muted">
            <strong>Tipo de sujeto DIAN:</strong>{' '}
            {additionalAccountID === '1' ? (
              <>
                Persona Jurídica (<code>AdditionalAccountID="1"</code>)
              </>
            ) : (
              <>
                Persona Natural (<code>AdditionalAccountID="2"</code>)
              </>
            )}{' '}
            — derivado automáticamente del tipo de identificación.
          </p>

          <Field label="Régimen tributario" htmlFor="tax_regime">
            <select
              id="tax_regime"
              value={s.tax_regime ?? ''}
              onChange={(e) => setS({ ...s, tax_regime: e.target.value })}
              className={inputCls}
            >
              {TAX_REGIMES.map((r) => (
                <option key={r.code} value={r.code}>
                  {r.code} — {r.label}
                </option>
              ))}
            </select>
          </Field>

          <div>
            <p className="text-sm font-medium">Responsabilidades fiscales</p>
            <div className="mt-2 space-y-2">
              {FISCAL_RESPONSIBILITIES.map((r) => {
                const has = (s.fiscal_responsibilities ?? []).includes(r.code)
                return (
                  <label
                    key={r.code}
                    className="flex cursor-pointer items-start gap-2 rounded-md border border-border bg-bg-app px-3 py-2 hover:bg-bg-elevated"
                  >
                    <input
                      type="checkbox"
                      checked={has}
                      onChange={() => {
                        const cur = s.fiscal_responsibilities ?? []
                        const next = has ? cur.filter((c) => c !== r.code) : [...cur, r.code]
                        setS({ ...s, fiscal_responsibilities: next })
                      }}
                      className="mt-0.5"
                    />
                    <span className="text-sm">
                      <span className="font-mono text-xs text-fg-muted">{r.code}</span>{' '}
                      {r.label}
                    </span>
                  </label>
                )
              })}
            </div>
          </div>

          <div className="flex items-center justify-between pt-2">
            {isEdit && (
              <button
                type="button"
                onClick={() => {
                  if (confirm('¿Archivar este cliente? Quedará oculto pero las facturas históricas lo conservan.')) {
                    archive.mutate()
                  }
                }}
                className="inline-flex items-center gap-1.5 text-xs text-error-fg hover:underline"
              >
                <Trash2 className="h-3.5 w-3.5" /> Archivar
              </button>
            )}
            <div className="ml-auto">
              <button
                type="submit"
                disabled={!valid || save.isPending}
                className="inline-flex items-center gap-1.5 rounded-md bg-brand-600 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-700 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {save.isPending ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" /> Guardando...
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4" /> {isEdit ? 'Guardar cambios' : 'Crear cliente'}
                  </>
                )}
              </button>
            </div>
          </div>
        </form>
      </div>
    </AppShell>
  )
}

function Field({
  label,
  htmlFor,
  children,
}: {
  label: string
  htmlFor: string
  children: React.ReactNode
}) {
  return (
    <div>
      <label htmlFor={htmlFor} className="block text-sm font-medium">
        {label}
      </label>
      <div className="mt-1">{children}</div>
    </div>
  )
}

const inputCls =
  'block w-full rounded-md border border-border bg-bg-app px-3 py-2 text-sm text-fg-primary placeholder-fg-muted focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500'
